/**
 * useWalletBalance — live-updating notification-credit wallet balance.
 *
 * Mirrors useNotifications.ts's reference pattern:
 * Primary delivery: AppSync subscription on `onWalletBalanceChanged` (push).
 * Fallback: REST polling — closes the gap if the WS drops silently. The
 * polling tick also runs immediately whenever the tab becomes visible again.
 *
 * The hook's public API stays the same regardless of which channel delivered
 * the data — components don't need to know.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";

import { CreditsAPI } from "../services/credits.service";
import { isPlatformAdminSession } from "../components/RequireAuth";
import { COMMS_CREDIT_COST_INR } from "../constants";
import { logger } from "../utils/logger";

// Fallback heartbeat. Shorter than useNotifications' 5-minute poll — a stale
// balance here doesn't just delay a badge, it can let a doctor try (and fail)
// a send the UI should have already blocked, or keep a "buy credits" banner
// up after the clinic has already topped up. 1 minute keeps that gap small
// without hammering the API.
const POLL_INTERVAL_MS = 60_000;

// "Low" threshold — headroom worth roughly 20 sends at the current per-send
// cost, so a clinic sees a warning before actually hitting zero mid-workday.
// Documented here rather than left as a bare number: 20 * ₹0.50 = ₹10 = 1000 paise.
export const LOW_BALANCE_THRESHOLD_PAISE = Math.round(20 * COMMS_CREDIT_COST_INR * 100);

// GraphQL subscription document. Inlined (rather than imported from a codegen
// file) because the AppSync schema is small and we don't yet run
// graphql-codegen in this repo — same convention as useNotifications.ts.
const ON_WALLET_BALANCE_CHANGED = /* GraphQL */ `
  subscription OnWalletBalanceChanged($clinicId: ID!) {
    onWalletBalanceChanged(clinicId: $clinicId) {
      clinicId
      balancePaise
    }
  }
`;

interface OnWalletBalanceChangedEvent {
  data?: {
    onWalletBalanceChanged?: {
      clinicId: string;
      balancePaise: number;
    } | null;
  };
}

export type WalletGateState = "sufficient" | "low" | "zero";

/** balancePaise === null means "unknown yet" — never block optimistically. */
export function gateStateForBalance(balancePaise: number | null): WalletGateState {
  if (balancePaise === null) return "sufficient";
  if (balancePaise <= 0) return "zero";
  if (balancePaise < LOW_BALANCE_THRESHOLD_PAISE) return "low";
  return "sufficient";
}

interface UseWalletBalanceReturn {
  balancePaise: number | null;
  updatedAt: string | null;
  gateState: WalletGateState;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useWalletBalance(): UseWalletBalanceReturn {
  const [balancePaise, setBalancePaise] = useState<number | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refetch = useCallback(async () => {
    // Checked per call, not once at mount: the global banner mounts before
    // sign-in, so the role is only known on later ticks.
    if (isPlatformAdminSession()) {
      if (mountedRef.current) setLoading(false);
      return;
    }
    try {
      const wallet = await CreditsAPI.getWallet();
      if (!mountedRef.current) return;
      setBalancePaise(wallet.balance_paise);
      setUpdatedAt(wallet.updated_at);
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      // Unauthenticated / no clinic / network error — leave balance as-is
      // (null on first load) rather than surfacing noisily; gateState then
      // defaults to "sufficient" so we never block a send on our own hiccup.
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();

    let subscription: { unsubscribe: () => void } | null = null;
    let cancelled = false;

    // ── AppSync subscription ────────────────────────────────────────────────
    // Needs the caller's clinicId as a subscription argument. Read it straight
    // off the Cognito ID token claim (same claim the AppSync resolver uses
    // server-side to scope delivery — resolvers/onWalletBalanceChanged.js)
    // instead of an extra REST round-trip just to subscribe.
    (async () => {
      let clinicId: string | undefined;
      try {
        const session = await fetchAuthSession({ forceRefresh: false });
        const claims = session.tokens?.idToken?.payload;
        clinicId = claims?.["custom:clinic_id"] as string | undefined;
      } catch {
        // Not authenticated — polling keeps this working in dev (AUTH_ENABLED=False).
      }
      if (cancelled || !clinicId) return;

      // generateClient throws or fails to connect when AppSync isn't configured
      // (e.g., before CDK deploy or if REACT_APP_APPSYNC_GRAPHQL_URL is empty).
      // We catch that and continue with polling only.
      try {
        const client = generateClient();
        const result = client.graphql({
          query: ON_WALLET_BALANCE_CHANGED,
          variables: { clinicId },
        });
        if (result && typeof (result as any).subscribe === "function") {
          subscription = (result as any).subscribe({
            next: (event: OnWalletBalanceChangedEvent) => {
              const incoming = event?.data?.onWalletBalanceChanged;
              if (!incoming || !mountedRef.current) return;
              setBalancePaise(incoming.balancePaise);
              setUpdatedAt(new Date().toISOString());
            },
            error: (err: unknown) => {
              logger.warn("Wallet balance subscription error", {
                error: err instanceof Error ? err.message : String(err),
              });
            },
          });
        }
      } catch (err) {
        logger.warn("Wallet balance subscription unavailable; falling back to polling only", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    // ── Polling heartbeat (only ticks when tab is visible) ────────────────
    const tick = () => {
      if (document.visibilityState === "visible") void refetch();
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refetch();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refetch]);

  return {
    balancePaise,
    updatedAt,
    gateState: gateStateForBalance(balancePaise),
    loading,
    error,
    refetch,
  };
}
